// Axial hex coordinates (pointy-top). Shared by client and server. FROZEN CONTRACT.

export interface Axial {
  q: number
  r: number
}

export const SQRT3 = Math.sqrt(3)

/** The six axial direction offsets, counter-clockwise starting east. */
export const DIRS: readonly Axial[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
]

export function hexKey(q: number, r: number): string {
  return q + ',' + r
}

export function parseHexKey(key: string): Axial {
  const i = key.indexOf(',')
  return { q: Number(key.slice(0, i)), r: Number(key.slice(i + 1)) }
}

export function neighbors(q: number, r: number): Axial[] {
  return DIRS.map((d) => ({ q: q + d.q, r: r + d.r }))
}

export function hexDist(aq: number, ar: number, bq: number, br: number): number {
  const dq = aq - bq
  const dr = ar - br
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2
}

/** Center of hex (q,r) in pixels for a given hex size (center-to-corner radius). */
export function hexToPixel(q: number, r: number, size: number): { x: number; y: number } {
  return { x: size * SQRT3 * (q + r / 2), y: size * 1.5 * r }
}

/** Fractional axial coords for a pixel position; use hexRound to snap. */
export function pixelToHexFrac(x: number, y: number, size: number): { q: number; r: number } {
  const q = ((SQRT3 / 3) * x - (1 / 3) * y) / size
  const r = ((2 / 3) * y) / size
  return { q, r }
}

export function hexRound(fq: number, fr: number): Axial {
  const fs = -fq - fr
  let q = Math.round(fq)
  let r = Math.round(fr)
  const s = Math.round(fs)
  const dq = Math.abs(q - fq)
  const dr = Math.abs(r - fr)
  const ds = Math.abs(s - fs)
  if (dq > dr && dq > ds) q = -r - s
  else if (dr > ds) r = -q - s
  return { q, r }
}

export function pixelToHex(x: number, y: number, size: number): Axial {
  const f = pixelToHexFrac(x, y, size)
  return hexRound(f.q, f.r)
}

/** Hexes at exactly `radius` from center (radius >= 1). */
export function hexRing(q: number, r: number, radius: number): Axial[] {
  const out: Axial[] = []
  let cq = q + DIRS[4].q * radius
  let cr = r + DIRS[4].r * radius
  for (let side = 0; side < 6; side++) {
    for (let step = 0; step < radius; step++) {
      out.push({ q: cq, r: cr })
      cq += DIRS[side].q
      cr += DIRS[side].r
    }
  }
  return out
}

/** Center plus all rings out to `radius`, nearest first. */
export function hexSpiral(q: number, r: number, radius: number): Axial[] {
  const out: Axial[] = [{ q, r }]
  for (let rad = 1; rad <= radius; rad++) out.push(...hexRing(q, r, rad))
  return out
}

// ---- Chunks ----------------------------------------------------------------
// The infinite map is generated/stored/streamed in square (in axial space)
// chunks of CHUNK x CHUNK tiles.

export const CHUNK = 16

export function chunkOf(q: number, r: number): { cx: number; cy: number } {
  return { cx: Math.floor(q / CHUNK), cy: Math.floor(r / CHUNK) }
}

export function chunkKey(cx: number, cy: number): string {
  return cx + ',' + cy
}

export function chunkKeyOf(q: number, r: number): string {
  const c = chunkOf(q, r)
  return chunkKey(c.cx, c.cy)
}

/** Index of tile (q,r) within its chunk's flat tiles array (row-major by r). */
export function localIdx(q: number, r: number): number {
  const lq = q - Math.floor(q / CHUNK) * CHUNK
  const lr = r - Math.floor(r / CHUNK) * CHUNK
  return lr * CHUNK + lq
}

/** Axial coords of the tile at flat index `i` of chunk (cx,cy). */
export function tileFromIdx(cx: number, cy: number, i: number): Axial {
  return { q: cx * CHUNK + (i % CHUNK), r: cy * CHUNK + Math.floor(i / CHUNK) }
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}
